<template>
  <div class="payment-overlay">

    <!-- Экран загрузки -->
    <transition name="fade">
      <div v-if="!resultReady" class="payment-screen">
        <div class="bg-blur-circle"></div>

        <div class="card-wrapper">
          <div class="card-glow-ring"></div>
          <div class="bank-card">
            <div class="card-chip">
              <div class="chip-lines"></div>
            </div>
            <div class="card-dots">
              <span v-for="n in 4" :key="'g'+n" class="dot-group">
                <span v-for="d in 4" :key="d" class="dot"></span>
              </span>
            </div>
            <div class="wifi-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" opacity="0.8">
                <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
              </svg>
            </div>
          </div>
        </div>

        <h2 class="status-title">{{ currentStatus }}<span class="dot-anim">{{ dots }}</span></h2>

        <div class="progress-bar-wrap">
          <div class="progress-bar-fill" :style="{ width: progress + '%' }"></div>
        </div>

        <p class="wait-text">Очікуйте відповідь <strong>{{ remainingTime }}</strong></p>

        <div class="secure-badge">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="#4caf50">
            <path d="M10 0L2 3V9C2 14 6 18 10 20C14 18 18 14 18 9V3L10 0Z"/>
          </svg>
          Захищене з'єднання з банком
        </div>
      </div>
    </transition>

    <!-- Экран результата -->
    <transition name="slide-up">
      <div v-if="resultReady" class="result-screen">
        <div class="result-icon" :class="resultSuccess ? 'success' : resultPartial ? 'partial' : 'fail'">
          <transition name="pop">
            <svg v-if="resultSuccess" viewBox="0 0 52 52" class="check-icon">
              <circle cx="26" cy="26" r="25" fill="none" stroke="#4caf50" stroke-width="2"/>
              <path class="check-path" fill="none" stroke="#4caf50" stroke-width="3" d="M14 27l8 8 16-16"/>
            </svg>
            <svg v-else-if="resultPartial" viewBox="0 0 52 52" class="check-icon">
              <circle cx="26" cy="26" r="25" fill="none" stroke="#ffb300" stroke-width="2"/>
              <path fill="none" stroke="#ffb300" stroke-width="3" d="M26 13 L26 30"/>
              <circle cx="26" cy="38" r="2.5" fill="#ffb300"/>
            </svg>
            <svg v-else viewBox="0 0 52 52" class="check-icon">
              <circle cx="26" cy="26" r="25" fill="none" stroke="#e53935" stroke-width="2"/>
              <path fill="none" stroke="#e53935" stroke-width="3" d="M16 16 L36 36 M36 16 L16 36"/>
            </svg>
          </transition>
        </div>

        <h2 class="result-title" :class="resultSuccess ? 'success-text' : resultPartial ? 'partial-text' : 'fail-text'">
          {{ resultTitle }}
        </h2>

        <p class="result-message">{{ resultMessage }}</p>

        <div v-if="resultDetails.length" class="result-details">
          <div
            v-for="item in resultDetails"
            :key="item.label"
            class="result-detail-row"
          >
            <span class="result-detail-label">{{ item.label }}</span>
            <span
              class="result-detail-value"
              :class="item.success ? 'success-text' : 'fail-text'"
            >
              {{ item.value }}
            </span>
          </div>
        </div>

        <div class="result-divider"></div>

        <p v-if="resultHint" class="redirect-hint">{{ resultHint }}</p>

        <button
          class="donora-pay-btn"
          type="button"
          @click="$emit('open-donora')"
        >
          <span class="donora-pay-heart" aria-hidden="true">
            <svg viewBox="0 0 64 64">
              <path
                d="M32 52S10 39 10 23c0-7 5-12 12-12 4 0 8 2 10 6 2-4 6-6 10-6 7 0 12 5 12 12 0 16-22 29-22 29Z"
              />
            </svg>
          </span>
          <span>{{ $t("pay.donoraCta") }}</span>
        </button>

        <button class="home-btn" @click="$emit('go-home')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
          На головну
        </button>
      </div>
    </transition>

  </div>
</template>

<script>
export default {
  name: "PaymentLoadingAnimation",
  props: {
    result: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentStatusIndex: 0,
      statuses: ["Підключення до банку", "Перевірка статусу", "Обробка платежу"],
      progress: 0,
      remainingTime: "2:00",
      totalSeconds: 120,
      dots: ".",
      progressInterval: null,
      statusInterval: null,
      timerInterval: null,
      dotsInterval: null,
      resultReady: false,
      resultSuccess: false,
      resultPartial: false,
      resultMessage: "",
      resultDetails: [],
    };
  },
  computed: {
    currentStatus() {
      return this.statuses[this.currentStatusIndex];
    },
    resultTitle() {
      if (this.resultSuccess) return "Оплату прийнято!";
      if (this.resultPartial) return "Статус оплат";
      return "Платіж відхилено";
    },
    resultHint() {
      if (this.resultSuccess) return "Дякуємо за оплату";
      if (this.resultPartial) return "";
      return "Спробуйте ще раз або оберіть іншу картку";
    },
  },
  watch: {
    result(val) {
      if (val !== null) {
        this.showResult(val);
      }
    },
  },
  mounted() {
    this.startAnimation();
  },
  beforeDestroy() {
    this.stopAnimation();
  },
  methods: {
    startAnimation() {
      this.progressInterval = setInterval(() => {
        if (this.progress < 90) this.progress += 0.4;
      }, 100);

      this.statusInterval = setInterval(() => {
        this.currentStatusIndex = (this.currentStatusIndex + 1) % this.statuses.length;
      }, 4000);

      this.timerInterval = setInterval(() => {
        if (this.totalSeconds > 0) {
          this.totalSeconds--;
          const m = Math.floor(this.totalSeconds / 60);
          const s = this.totalSeconds % 60;
          this.remainingTime = `${m}:${s.toString().padStart(2, "0")}`;
        }
      }, 1000);

      this.dotsInterval = setInterval(() => {
        this.dots = this.dots.length >= 3 ? "." : this.dots + ".";
      }, 500);
    },
    stopAnimation() {
      clearInterval(this.progressInterval);
      clearInterval(this.statusInterval);
      clearInterval(this.timerInterval);
      clearInterval(this.dotsInterval);
    },
    getFriendlyMessage(result) {
      const fallback = result.success
        ? "Платіж успішно оброблено"
        : "Платіж не вдалося обробити. Спробуйте ще раз або оберіть іншу картку.";
      const rawMessage = String(result.message || result.error || "").trim();
      const message = rawMessage.toLowerCase();

      if (!rawMessage) return fallback;

      if (
        message.includes("stripe") ||
        message.includes("3ds") ||
        message.includes("client_secret")
      ) {
        return "Підтвердження в банку скасовано або не завершено. Спробуйте ще раз і підтвердіть дію в застосунку банку.";
      }

      if (
        message.includes("your card was declined") ||
        message.includes("card was declined") ||
        message.includes("declined") ||
        message.includes("do_not_honor") ||
        message.includes("insufficient") ||
        message.includes("недостат") ||
        message.includes("відхил") ||
        message.includes("отклон")
      ) {
        return "Банк відхилив платіж. Перевірте картку, ліміт або оберіть іншу картку.";
      }

      if (
        message.includes("network") ||
        message.includes("timeout") ||
        message.includes("server") ||
        message.includes("exception")
      ) {
        return "Не вдалося отримати відповідь від банку. Перевірте статус платежу або спробуйте ще раз.";
      }

      return rawMessage;
    },
    showResult(result) {
      this.stopAnimation();
      this.progress = 100;
      this.resultPartial =
        result.partial === true || result.partial_success === true;
      this.resultSuccess = result.success === true && !this.resultPartial;
      this.resultMessage = this.getFriendlyMessage(result);
      this.resultDetails = Array.isArray(result.details) ? result.details : [];
      setTimeout(() => {
        this.resultReady = true;
      }, 400);
    },
  },
};
</script>

<style scoped>
.payment-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(160deg, #060b11 0%, #0e1628 60%, #060b11 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

/* === LOADING SCREEN === */
.payment-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 24px;
  text-align: center;
  box-sizing: border-box;
}

.bg-blur-circle {
  position: absolute;
  width: min(260px, 70vw);
  height: min(260px, 70vw);
  background: radial-gradient(circle, rgba(68, 119, 246, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  top: 42%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse-bg 3s ease-in-out infinite;
  pointer-events: none;
}

/* Карта */
.card-wrapper {
  position: relative;
  width: min(160px, 42vw);
  height: min(100px, 26vw);
  margin-bottom: clamp(16px, 4vw, 24px);
}

.card-glow-ring {
  position: absolute;
  inset: -18px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(68, 119, 246, 0.25) 0%, transparent 65%);
  animation: pulse-bg 2.5s ease-in-out infinite;
}

.bank-card {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #2653c7 0%, #4477f6 60%, #6a9bff 100%);
  border-radius: clamp(10px, 3vw, 14px);
  padding: clamp(10px, 3vw, 14px) clamp(12px, 3.5vw, 16px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6), 0 0 30px rgba(68, 119, 246, 0.25);
  animation: float 4s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card-chip {
  width: clamp(24px, 7vw, 32px);
  height: clamp(18px, 5vw, 24px);
  background: linear-gradient(135deg, #f5c842 0%, #e8a000 100%);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.chip-lines::before,
.chip-lines::after {
  content: "";
  position: absolute;
  background: rgba(0, 0, 0, 0.15);
}
.chip-lines::before {
  width: 100%;
  height: 1px;
  top: 50%;
  left: 0;
}
.chip-lines::after {
  width: 1px;
  height: 100%;
  top: 0;
  left: 50%;
}

.card-dots {
  display: flex;
  gap: clamp(4px, 1.5vw, 6px);
  align-items: center;
}

.dot-group {
  display: flex;
  gap: 2px;
}

.dot {
  width: clamp(3px, 1vw, 4px);
  height: clamp(3px, 1vw, 4px);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
}

.wifi-icon {
  position: absolute;
  right: 12px;
  top: 12px;
  opacity: 0.7;
  animation: wifi-pulse 2s ease-in-out infinite;
}

/* Текст */
.status-title {
  color: #ffffff;
  font-size: clamp(13px, 3.8vw, 15px);
  font-weight: 600;
  margin-bottom: 14px;
  letter-spacing: 0.2px;
}

.dot-anim {
  display: inline-block;
  width: 20px;
  text-align: left;
  color: #4477f6;
}

/* Прогресс */
.progress-bar-wrap {
  width: 100%;
  max-width: min(220px, 60vw);
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #2653c7, #4477f6, #6a9bff);
  border-radius: 10px;
  transition: width 0.3s linear;
  box-shadow: 0 0 10px rgba(68, 119, 246, 0.7);
}

.wait-text {
  color: rgba(255, 255, 255, 0.45);
  font-size: clamp(11px, 3vw, 12px);
  margin-bottom: clamp(14px, 4vw, 22px);
}

.wait-text strong {
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(12px, 3.2vw, 13px);
}

.secure-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: rgba(76, 175, 80, 0.07);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: 30px;
  color: rgba(255, 255, 255, 0.6);
  font-size: clamp(10px, 2.8vw, 12px);
}

/* === RESULT SCREEN === */
.result-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;
  text-align: center;
  box-sizing: border-box;
}

.result-icon {
  width: clamp(60px, 18vw, 75px);
  height: clamp(60px, 18vw, 75px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: clamp(16px, 4vw, 22px);
}

.result-icon.success {
  background: rgba(76, 175, 80, 0.12);
  box-shadow: 0 0 50px rgba(76, 175, 80, 0.25);
}

.result-icon.fail {
  background: rgba(229, 57, 53, 0.12);
  box-shadow: 0 0 50px rgba(229, 57, 53, 0.25);
}

.result-icon.partial {
  background: rgba(255, 179, 0, 0.12);
  box-shadow: 0 0 50px rgba(255, 179, 0, 0.25);
}

.check-icon {
  width: clamp(42px, 13vw, 55px);
  height: clamp(42px, 13vw, 55px);
}

.result-title {
  font-size: clamp(18px, 5vw, 22px);
  font-weight: 700;
  margin-bottom: 12px;
}

.success-text { color: #4caf50; }
.partial-text { color: #ffca28; }
.fail-text { color: #ef5350; }

.result-message {
  color: rgba(255, 255, 255, 0.65);
  font-size: clamp(13px, 3.5vw, 14px);
  line-height: 1.5;
  max-width: min(260px, 80vw);
  margin-bottom: clamp(16px, 4vw, 24px);
}

.result-details {
  width: min(300px, 84vw);
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: clamp(16px, 4vw, 22px);
}

.result-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.result-detail-label {
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  font-weight: 600;
  text-align: left;
}

.result-detail-value {
  font-size: 13px;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

.result-divider {
  width: 60px;
  height: 3px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
}

.redirect-hint {
  color: rgba(255, 255, 255, 0.35);
  font-size: clamp(11px, 3vw, 13px);
  margin-bottom: clamp(20px, 5vw, 28px);
  max-width: min(280px, 80vw);
}

.home-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: min(300px, 80vw);
  padding: clamp(14px, 4vw, 18px) 32px;
  background: linear-gradient(135deg, #2653c7, #4477f6) !important;
  border: none !important;
  border-radius: 50px !important;
  color: #ffffff !important;
  font-size: clamp(15px, 4vw, 18px);
  font-weight: 700;
  cursor: pointer;
  height: auto !important;
  box-shadow: 0 8px 24px rgba(68, 119, 246, 0.4) !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.home-btn svg {
  width: clamp(18px, 4.5vw, 22px);
  height: clamp(18px, 4.5vw, 22px);
  flex-shrink: 0;
}

.home-btn:active {
  transform: scale(0.96);
  box-shadow: 0 4px 12px rgba(68, 119, 246, 0.3) !important;
}

/* === Анимации === */
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(-1deg); }
  50% { transform: translateY(-12px) rotate(1deg); }
}

@keyframes pulse-bg {
  0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
}

@keyframes wifi-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.4s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.slide-up-enter-active { transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
.slide-up-enter { opacity: 0; transform: translateY(40px) scale(0.9); }

.pop-enter-active { transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.pop-enter { opacity: 0; transform: scale(0.3); }

.check-path {
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  animation: draw-check 0.5s ease forwards 0.3s;
}

@keyframes draw-check {
  to { stroke-dashoffset: 0; }
}

@media (max-width: 320px) {
  .payment-screen {
    padding: 0 16px;
  }
  .result-screen {
    padding: 0 16px;
  }
  .secure-badge {
    padding: 7px 12px;
  }
}

.donora-pay-btn {
  order: 20;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: min(300px, 80vw);
  min-height: 54px;
  margin-top: 22px;
  padding: clamp(14px, 4vw, 18px) 32px;
  border: none !important;
  border-radius: 50px !important;
  background: linear-gradient(135deg, #2653c7, #4477f6) !important;
  color: #ffffff !important;
  font-size: clamp(15px, 4vw, 18px);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0;
  box-shadow: 0 8px 24px rgba(68, 119, 246, 0.4) !important;
  cursor: pointer;
}

.home-btn {
  order: 10;
}

.donora-pay-btn::before {
  content: "";
  position: absolute;
  top: -12px;
  left: 12%;
  right: 12%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.24),
    transparent
  );
}

.donora-pay-btn:active {
  transform: scale(0.96);
}

.donora-pay-heart {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  animation: donora-heart-pulse 1.35s ease-in-out infinite;
}

.donora-pay-heart svg {
  width: 23px;
  height: 23px;
  fill: none;
  stroke: currentColor;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.38));
}

@keyframes donora-heart-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  45% {
    transform: scale(1.12);
  }
}
</style>
